import React, { useEffect, useState } from "react";
import { DBApplication } from "../../interfaces/CoObject";
import { fetchDimensions } from "../../../api/object/Dimensions";
import { Dimension } from "../../../api/models/dimension";
import { Toggle } from "@fluentui/react/lib/Toggle";
import {
  CollapsableCardDefaultHeaderComponent,
  CollapsableCardHeaderComponentProps,
} from "../../layout/CollapsableCard";
import { DimensionParameter } from "../../../api/models/dimension-parameter";
import { DimensionDetail } from "../../../api/models/dimension-detail";

export interface DocumentParametersCardProps {
  selectedDbApplication: DBApplication;
  updateDimensionFromTree: (dimensionItem: Dimension, newDimensions: DimensionDetail[] | undefined) => void;
  dimensions: DimensionDetail[] | undefined;
  enableDbApplication: (enable: boolean) => void;
  inheritableDbParameters: DimensionParameter | undefined;
  canInherit: boolean;
  readonly?: boolean;
}

interface DocumentParametersCardState {
  expandCard: boolean;
  availableDimensions: Dimension[] | undefined;
  selectedDimensionsItems: Dimension[];
  isContextualMenuOpen: boolean;
  loadedDbId: string | undefined;
}

const useDocumentParametersCard = (props: DocumentParametersCardProps) => {
  const {
    selectedDbApplication,
    updateDimensionFromTree,
    dimensions,
    enableDbApplication,
    inheritableDbParameters,
    canInherit,
    readonly,
  } = props;

  const addDimensionLinkRef = React.useRef(null);
  const [state, setState] = useState<DocumentParametersCardState>({
    expandCard: false,
    availableDimensions: undefined,
    isContextualMenuOpen: false,
    selectedDimensionsItems: [],
    loadedDbId: undefined,
  });

  const getHeaderComponent = ({
    isExpanded,
    setExpanded,
    dimensions,
    onChangeToggle,
    selectedDbApplication,
    readonly,
  }: CollapsableCardHeaderComponentProps & {
    dimensions: DimensionDetail[];
    onChangeToggle: (_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => void;
    selectedDbApplication: DBApplication;
    readonly?: boolean;
  }) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Toggle
        disabled={!!readonly}
        checked={dimensions !== undefined}
        onChange={onChangeToggle}
        styles={{ root: { marginBottom: 0 } }}
      />
      <span style={{ height: "100%", textAlign: "center", flexGrow: 1 }}>{selectedDbApplication.desc}</span>
      {dimensions && <CollapsableCardDefaultHeaderComponent isExpanded={isExpanded} setExpanded={setExpanded} />}
    </div>
  );

  useEffect(() => {
    if (selectedDbApplication.id !== state.loadedDbId && dimensions)
      fetchDimensions(selectedDbApplication.id, true).then((dimensionsDetails) => {
        const defaultSelectedDimensions = dimensionsDetails.filter(
          (d) => dimensions?.find((value) => value.dimensionOid === d.oid) !== undefined
        );
        setState((s) => ({
          ...s,
          loadedDbId: selectedDbApplication.id,
          availableDimensions: dimensionsDetails,
          selectedDimensionsItems: defaultSelectedDimensions,
        }));
      });
  }, [selectedDbApplication, dimensions]);

  const filteredDimensions = state.availableDimensions?.filter(
    (dimension) =>
      state.selectedDimensionsItems.find((presentItem) => dimension.desc === presentItem.desc) === undefined //not default and not previously selected
  );

  const onChangeToggle = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
    enableDbApplication(checked);
    setState((s) => ({
      ...s,
      expandCard: checked ?? false,
      selectedDimensionsItems: checked ? s.selectedDimensionsItems : [],
    }));
  };

  const removeDimension = (dimensionItem: Dimension) => {
    updateDimensionFromTree(dimensionItem, undefined);
  };

  return {
    state,
    setState,
    dimensions,
    onChangeToggle,
    selectedDbApplication,
    canInherit,
    inheritableDbParameters,
    updateDimensionFromTree,
    removeDimension,
    filteredDimensions,
    addDimensionLinkRef,
    getHeaderComponent,
    readonly,
  };
};

export default useDocumentParametersCard;
